import React from 'react';
import { toast } from 'react-toastify';
import Loader from '~/components/common/parts/loading/Loading';
import { DefaultV2 } from '~/components/common/layouts/DefaultV2';
import { DealLayout } from '~/components/common/layouts/deals/DealLayout';
import { BreadcrumbItem } from '~/components/common/parts/breadcrumb/Breadcrumbs';
import { PATH as DEALS_PATH } from '~/pages/deals';
import Styles from './Page.module.scss';
import { DealCard } from '~/components/common/deals/show/deal_card/DealCard';
import { DealInspectionClientCard } from '~/components/common/deals/show/inspection_card/DealInspectionClientCard';
import { DealConstructionScheduleClientCard } from '~/components/common/deals/show/construction_card/DealConstructionScheduleClientCard';
import { DealMain } from '~/components/common/deals/show/main/DealMain';
import { useDeals } from '~/hooks/useDeals';
import { DealOriginVendorClientCard } from '~/components/common/deals/show/vendor_card/DealOriginVendorClientCard';
import { useMessages } from '~/hooks/deals/useMessages';
import { ChatPopContainer } from '~/components/common/chat/ChatPopContainer';
import { useOpenHandler } from '~/hooks/useOpenHandler';
import { useIdQueryHandler } from '~/hooks/useIdQueryHandler';
import { DealRepairCard } from '~/components/common/deals/show/repair_card/DealRepairCard';
import { DealVendorNoteCard } from '~/components/common/deals/show/deal_card/DealVendorNoteCard';
import { postTemporaryUnread } from '~/utils/api/deals/Message';
import { isSuccess } from '~/utils/api/api_handler';
import useItandiAccount from '~/hooks/useItandiAccount';
import { UPDATE_TEMPORARY_UNREAD_MESSAGE } from '~/models/Message';
import { SERVER_ERROR_MESSAGE } from '~/constants/AppConst';

export const Page: React.FC = () => {
  const id = useIdQueryHandler();
  const { isOpen, handleClose, handleOpen } = useOpenHandler();
  const { data: deal, mutate: mutateDeal, isLoading } = useDeals().show(id);
  const { data: messages, mutate: mutateMessages } = useMessages().index(id);
  const { data: account } = useItandiAccount().currentAccount();
  const handleToggleChat = React.useCallback(() => {
    if (isOpen) {
      handleClose();
    } else {
      handleOpen();
    }
  }, [isOpen, handleClose, handleOpen]);
  const handleMutate = React.useCallback(async () => {
    mutateDeal && (await mutateDeal());
  }, [mutateDeal]);
  const handleMutateMessage = React.useCallback(async () => {
    mutateMessages && (await mutateMessages());
    mutateDeal && (await mutateDeal());
  }, [mutateDeal, mutateMessages]);
  if (isLoading || deal === null || account === null) {
    return <Loader isLoading />;
  }
  const handleTemporaryUnread = async (): Promise<void> => {
    const result = await postTemporaryUnread(deal.id);
    if (isSuccess(result)) {
      toast.success(UPDATE_TEMPORARY_UNREAD_MESSAGE);
      mutateDeal && (await mutateDeal());
      handleClose();
      return;
    }
    toast.error(SERVER_ERROR_MESSAGE);
  };
  const breadcrumbItems: BreadcrumbItem[] = [
    { path: DEALS_PATH, caption: '案件一覧', active: false },
    { path: null, caption: '案件詳細', active: true }
  ];
  return (
    <DefaultV2>
      <DealLayout
        breadcrumbItems={breadcrumbItems}
        deal={deal}
        isVendor={false}
        noBackground
      >
        <div className={Styles.Container}>
          <div className={Styles.LeftCol}>
            <DealOriginVendorClientCard handleToggleChat={handleToggleChat} />
            <DealVendorNoteCard deal={deal} />
            <DealCard
              deal={deal}
              handleMutate={handleMutate}
              isVendor={false}
            />
            {deal.dealType === 'repair' && (
              <DealRepairCard
                companyType="client"
                deal={deal}
                handleMutate={handleMutate}
              />
            )}
            <DealInspectionClientCard />
            <DealConstructionScheduleClientCard />
          </div>
          <DealMain deal={deal} isAdmin={account.isAdmin} isVendor={false} />
        </div>
      </DealLayout>
      {messages !== null && (
        <ChatPopContainer
          deal={deal}
          handleClose={handleClose}
          handleMutate={handleMutateMessage}
          handleTemporaryUnread={handleTemporaryUnread}
          isAdmin={account.isAdmin}
          isOpen={isOpen}
          messages={messages}
          speaker="maintenance_company"
        />
      )}
    </DefaultV2>
  );
};
